import React, { useState, useEffect } from 'react';
import { Users, Shield, Package, ChevronRight, Check, X, RefreshCw } from 'lucide-react';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';
import {
  listPlans, listModules, listSubscriptions,
  getClientSubscription, changeClientPlan, toggleClientModule
} from '../../services/clientPlansService';

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD = '#b48c2c';

const ClientPlansTab = () => {
  const { lang } = useGovernanceLang();
  const L = {
    pt: {
      catalog: 'Catálogo de Planos', free: 'Grátis', perMonth: '/mês',
      videos: 'vídeos', users: 'usuários', athletes: 'atletas',
      activeClients: 'Clientes Ativos',
      noPlan: 'Nenhum cliente com assinatura ativa. Use a SP <code>sp_change_client_plan</code> para atribuir planos.',
      colPlan: 'Plano', colStatus: 'Status', colModules: 'Módulos', colAmount: 'Valor',
      changePlan: 'Trocar Plano', authorizedModules: 'Módulos Autorizados',
      planHistory: 'Histórico de Planos',
      promptReason: 'Motivo da troca de plano (opcional):',
    },
    en: {
      catalog: 'Plan Catalog', free: 'Free', perMonth: '/mo',
      videos: 'videos', users: 'users', athletes: 'athletes',
      activeClients: 'Active Clients',
      noPlan: 'No clients with an active subscription. Use SP <code>sp_change_client_plan</code> to assign plans.',
      colPlan: 'Plan', colStatus: 'Status', colModules: 'Modules', colAmount: 'Amount',
      changePlan: 'Change Plan', authorizedModules: 'Authorized Modules',
      planHistory: 'Plan History',
      promptReason: 'Reason for plan change (optional):',
    },
  }[lang] || {};
  const [plans, setPlans] = useState([]);
  const [modules, setModules] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changingPlan, setChangingPlan] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [plansRes, modulesRes, subsRes] = await Promise.all([
        listPlans(), listModules(), listSubscriptions()
      ]);
      setPlans(plansRes?.data || []);
      setModules(modulesRes?.data || []);
      setSubscriptions(subsRes?.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const loadClientDetail = async (institutionId) => {
    setSelectedClient(institutionId);
    try {
      const res = await getClientSubscription(institutionId);
      setClientDetail(res?.data || null);
    } catch (err) { console.error(err); }
  };

  const handleChangePlan = async (institutionId, planCode) => {
    const reason = prompt(L.promptReason);
    setChangingPlan(true);
    try {
      await changeClientPlan(institutionId, planCode, reason || '');
      await loadClientDetail(institutionId);
      await loadData();
    } catch (err) { console.error(err); }
    setChangingPlan(false);
  };

  const handleToggleModule = async (institutionId, moduleCode, currentEnabled) => {
    try {
      await toggleClientModule(institutionId, moduleCode, !currentEnabled);
      await loadClientDetail(institutionId);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { loadData(); }, []);

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="bg-white h-24 rounded-xl animate-pulse" />)}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Plans Catalog */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2"><Package size={18} /> {L.catalog}</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {plans.map(p => (
            <div key={p.id} className="bg-white rounded-xl border border-gray-200 p-4 text-center">
              <h4 className="font-bold text-gray-900">{p.plan_name}</h4>
              <div className="text-2xl font-extrabold mt-1" style={{ color: BRAND_GREEN }}>
                {p.price_brl > 0 ? `R$${p.price_brl.toLocaleString()}` : L.free}
              </div>
              <div className="text-xs text-gray-500 mt-1">{p.billing_cycle}</div>
              <div className="mt-2 text-xs text-gray-600 space-y-0.5">
                <div>{p.max_athletes ? `${p.max_athletes} ${L.athletes}` : `∞ ${L.athletes}`}</div>
                <div>{p.max_videos_month ? `${p.max_videos_month} ${L.videos}${L.perMonth}` : `∞ ${L.videos}`}</div>
                <div>{p.max_users ? `${p.max_users} ${L.users}` : `∞ ${L.users}`}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Client Subscriptions List */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2"><Users size={18} /> {L.activeClients}</h3>
        {subscriptions.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500"
            dangerouslySetInnerHTML={{ __html: L.noPlan }} />
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Institution ID</th>
                  <th className="px-4 py-3 text-left font-semibold">{L.colPlan}</th>
                  <th className="px-4 py-3 text-left font-semibold">{L.colStatus}</th>
                  <th className="px-4 py-3 text-center font-semibold">{L.colModules}</th>
                  <th className="px-4 py-3 text-right font-semibold">{L.colAmount}</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map(s => (
                  <tr key={s.institution_id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => loadClientDetail(s.institution_id)}>
                    <td className="px-4 py-3 font-mono">#{s.institution_id}</td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 rounded text-xs font-bold bg-green-100 text-green-800">{s.plan_name}</span></td>
                    <td className="px-4 py-3"><span className={`text-xs ${s.subscription_status === 'active' ? 'text-green-600' : 'text-yellow-600'}`}>{s.subscription_status}</span></td>
                    <td className="px-4 py-3 text-center font-bold">{s.active_modules || 0}</td>
                    <td className="px-4 py-3 text-right font-mono">R${(s.plan_price_brl || 0).toLocaleString()}</td>
                    <td className="px-4 py-3"><ChevronRight size={14} className="text-gray-400" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Client Detail Panel */}
      {selectedClient && clientDetail && (
        <div className="bg-white rounded-xl border-2 border-green-200 p-5 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold" style={{ color: BRAND_GREEN }}>
              Cliente #{selectedClient}
            </h3>
            <button onClick={() => { setSelectedClient(null); setClientDetail(null); }} className="text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          </div>

          {/* Change Plan */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-2">{L.changePlan}</h4>
            <div className="flex gap-2 flex-wrap">
              {plans.map(p => {
                const isCurrentPlan = clientDetail.subscription?.plan_code === p.plan_code;
                return (
                  <button
                    key={p.id}
                    disabled={isCurrentPlan || changingPlan}
                    onClick={() => handleChangePlan(selectedClient, p.plan_code)}
                    className={`px-4 py-2 rounded-lg text-sm font-bold border transition-all ${
                      isCurrentPlan
                        ? 'bg-green-100 border-green-300 text-green-800 cursor-default'
                        : 'border-gray-200 hover:border-green-400 hover:bg-green-50 text-gray-700'
                    }`}
                  >
                    {isCurrentPlan && <Check size={12} className="inline mr-1" />}
                    {p.plan_name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Module Toggles */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2"><Shield size={14} /> {L.authorizedModules}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {modules.map(mod => {
                const entitlement = (clientDetail.entitlements || []).find(e => e.module_code === mod.module_code);
                const isEnabled = entitlement?.is_enabled ?? false;
                return (
                  <div key={mod.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:border-gray-200">
                    <div>
                      <span className="text-sm font-medium text-gray-900">{mod.module_name}</span>
                      <span className="text-xs text-gray-500 ml-2">({mod.category})</span>
                    </div>
                    <button
                      onClick={() => handleToggleModule(selectedClient, mod.module_code, isEnabled)}
                      className={`w-12 h-6 rounded-full relative transition-colors ${isEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                    >
                      <div className={`w-5 h-5 rounded-full bg-white shadow absolute top-0.5 transition-transform ${isEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* History */}
          {clientDetail.history?.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-700 mb-2">{L.planHistory}</h4>
              <div className="space-y-1">
                {clientDetail.history.map((h, i) => (
                  <div key={i} className="text-xs text-gray-600 flex gap-2">
                    <span className="text-gray-400">{h.changed_at?.slice(0, 10)}</span>
                    <span>{h.old_plan_name || '—'} → <strong>{h.new_plan_name}</strong></span>
                    <span className="text-gray-400">por {h.changed_by}</span>
                    {h.reason && <span className="italic">"{h.reason}"</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientPlansTab;
